import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { GoogleReviewsFooterBadge } from "@/components/GoogleReviewsBadge";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Gallery", path: "/gallery" },
  { label: "Contact", path: "/contact" },
];

const services = [
  "Lawn Cutting & Edging",
  "Landscaping Design",
  "Mulching & Planting",
  "Seasonal Cleanup",
  "Snow Removal & Salting",
  "Driveway Sealing",
  "Fence Staining",
  "Deck Cleaning",
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#1F291E] text-[#F9F8F6]">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img
                src="/logo.svg"
                alt="Absolute Landscapers"
                className="h-10 w-auto brightness-[2] saturate-50"
              />
            </div>
            <p className="text-[#a0b89e] text-sm leading-relaxed mb-5">
              Mississauga's trusted residential landscaping experts. Transforming outdoor spaces across the GTA with quality and care.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#8dc63f] flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#8dc63f] flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
            </div>
            {/* Google Reviews compact badge */}
            <div className="mt-5">
              <GoogleReviewsFooterBadge />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="font-semibold text-white mb-5 text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-[#a0b89e] text-sm hover:text-[#8dc63f] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4
              className="font-semibold text-white mb-5 text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="text-[#a0b89e] text-sm hover:text-[#8dc63f] transition-colors"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="font-semibold text-white mb-5 text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#8dc63f] mt-0.5 shrink-0" />
                <span className="text-[#a0b89e] text-sm">
                  2 Robert Speck Pkwy<br />L4Z 1H8 Mississauga, ON
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-[#8dc63f] shrink-0" />
                <a
                  href="tel:+16472422860"
                  data-testid="footer-phone"
                  className="text-[#a0b89e] text-sm hover:text-[#8dc63f] transition-colors"
                >
                  +1 647.242.2860
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#8dc63f] shrink-0" />
                <a
                  href="mailto:info@absolutelandscapers.ca"
                  data-testid="footer-email"
                  className="text-[#a0b89e] text-sm hover:text-[#8dc63f] transition-colors"
                >
                  info@absolutelandscapers.ca
                </a>
              </li>
            </ul>
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="inline-block mt-6 btn-accent px-5 py-2.5 rounded-lg text-sm"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[#a0b89e] text-xs">
            &copy; {new Date().getFullYear()} Absolute Landscapers. All rights reserved.
          </p>
          <p className="text-[#a0b89e] text-xs">
            Serving Mississauga, Brampton, Oakville & Surrounding GTA
          </p>
        </div>
      </div>
    </footer>
  );
}
